import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { api } from "../services/api";
import { toast } from "react-toastify";

const PracticeMCQsLayer = () => {
  // State for categories and subcategories from database
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for selected category and subcategory
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState("All");
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editQuestionIndex, setEditQuestionIndex] = useState(null);

  // State for viewing question details
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  // New question form
  const [newQuestion, setNewQuestion] = useState({
    questionText: "",
    options: ["", "", "", ""],
    correctAnswer: 0,
    explanation: "",
    image: "",
    categoryId: "",
    subcategoryId: "",
  });

  // State for image upload preview
  const [imagePreview, setImagePreview] = useState(null);

  // State for practice MCQs from API
  const [practiceMCQs, setPracticeMCQs] = useState([]);

  // Filtered MCQs based on selected category and subcategory
  const [filteredMCQs, setFilteredMCQs] = useState([]);

  // Search term for filtering questions
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch categories and subcategories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        // Get all categories
        const categoriesResponse = await api.categories.getAll();

        if (categoriesResponse.success) {
          setCategories(categoriesResponse.data);

          // Get subcategories for each category
          const subcategoriesMap = {};
          for (const category of categoriesResponse.data) {
            const subcategoriesResponse = await api.categories.getSubcategories(
              category.id
            );
            if (subcategoriesResponse.success) {
              subcategoriesMap[category.id] = subcategoriesResponse.data;
            }
          }

          setSubcategories(subcategoriesMap);
        } else {
          setError(categoriesResponse.message || "Failed to fetch categories");
        }
      } catch (err) {
        setError("Error connecting to the server");
        console.error("Error fetching categories:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Fetch practice MCQs from API
  useEffect(() => {
    const fetchPracticeQuestions = async () => {
      try {
        setLoading(true);

        // Prepare filters
        const filters = {};
        if (selectedCategoryId) filters.categoryId = selectedCategoryId;
        if (selectedSubcategoryId)
          filters.subcategoryId = selectedSubcategoryId;

        // Get practice questions
        const questionsResponse = await api.practiceQuestions.getAll(filters);

        if (questionsResponse.success) {
          setPracticeMCQs(questionsResponse.data);
        } else {
          setError(
            questionsResponse.message || "Failed to fetch practice questions"
          );
          toast.error(
            questionsResponse.message || "Failed to fetch practice questions"
          );
        }
      } catch (err) {
        setError("Error connecting to the server");
        console.error("Error fetching practice questions:", err);
        toast.error("Error connecting to the server");
      } finally {
        setLoading(false);
      }
    };

    fetchPracticeQuestions();
  }, [selectedCategoryId, selectedSubcategoryId]);

  // Filter MCQs based on search term
  useEffect(() => {
    let filtered = [...practiceMCQs];

    // Apply search term filter
    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (mcq) =>
          mcq.questionText.toLowerCase().includes(term) ||
          mcq.options.some((option) => option.toLowerCase().includes(term)) ||
          (mcq.explanation && mcq.explanation.toLowerCase().includes(term))
      );
    }

    setFilteredMCQs(filtered);
  }, [practiceMCQs, searchTerm]);

  // Handler for category change
  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    if (categoryId === "All") {
      setSelectedCategory("All");
      setSelectedCategoryId(null);
    } else {
      const category = categories.find((c) => c.id === categoryId);
      setSelectedCategory(category.name);
      setSelectedCategoryId(categoryId);
    }
    setSelectedSubcategory("All");
    setSelectedSubcategoryId(null);
  };

  // Handler for subcategory change
  const handleSubcategoryChange = (e) => {
    const subcategoryId = e.target.value;
    if (subcategoryId === "All") {
      setSelectedSubcategory("All");
      setSelectedSubcategoryId(null);
    } else {
      const subcategory = subcategories[selectedCategoryId].find(
        (sc) => sc.id === subcategoryId
      );
      setSelectedSubcategory(subcategory.name);
      setSelectedSubcategoryId(subcategoryId);
    }
  };

  // Add new question
  const addQuestion = () => {
    setEditQuestionIndex(null);
    setNewQuestion({
      questionText: "",
      options: ["", "", "", ""],
      correctAnswer: 0,
      explanation: "",
      image: "",
      categoryId:
        selectedCategoryId || (categories.length > 0 ? categories[0].id : ""),
      subcategoryId: selectedSubcategoryId || "",
    });
    setShowAddModal(true);
  };

  // Edit existing question
  const editQuestion = (index) => {
    setEditQuestionIndex(index);
    const question = filteredMCQs[index];

    setNewQuestion({
      id: question.id,
      questionText: question.questionText,
      options: [...question.options], // Create a copy to avoid modifying the original
      correctAnswer: question.correctAnswer,
      explanation: question.explanation || "",
      image: question.image || "",
      categoryId: question.categoryId,
      subcategoryId: question.subcategoryId || "",
    });

    // Set image preview if image exists
    if (question.image) {
      if (question.image.startsWith("data:")) {
        // If it's already a data URL (from a new upload)
        setImagePreview(question.image);
      } else if (question.image.startsWith("http")) {
        // If it's an external URL
        setImagePreview(question.image);
      } else {
        // If it's a server path
        setImagePreview(`${api.baseURL.replace("/api", "")}${question.image}`);
      }
    } else {
      setImagePreview(null);
    }

    setShowAddModal(true);
  };

  // Delete question
  const deleteQuestion = async (id) => {
    if (window.confirm("Are you sure you want to delete this question?")) {
      try {
        setLoading(true);
        const response = await api.practiceQuestions.delete(id);

        if (response.success) {
          // Remove question from state
          setPracticeMCQs(practiceMCQs.filter((q) => q.id !== id));
          toast.success("Question deleted successfully");
        } else {
          setError(response.message || "Failed to delete question");
          toast.error(response.message || "Failed to delete question");
        }
      } catch (err) {
        setError("Error connecting to the server");
        console.error("Error deleting question:", err);
        toast.error("Error connecting to the server");
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle form input changes
  const handleQuestionChange = (e) => {
    const { name, value } = e.target;
    setNewQuestion({ ...newQuestion, [name]: value });

    // If category changes, update subcategory options
    if (name === "categoryId") {
      setNewQuestion((prev) => ({
        ...prev,
        subcategoryId:
          subcategories[value] && subcategories[value].length > 0
            ? subcategories[value][0].id
            : "",
      }));
    }
  };

  // Handle option changes
  const handleOptionChange = (index, value) => {
    const newOptions = [...newQuestion.options];
    newOptions[index] = value;
    setNewQuestion({ ...newQuestion, options: newOptions });
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    // Check file type
    if (!file.type.match("image.*")) {
      toast.error("Please select an image file");
      return;
    }

    // Create a URL for preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Create FormData object
    const formData = new FormData();
    formData.append("image", file);

    // Inform user that upload is in progress
    toast.info("Uploading image...");

    // If we're updating a question, use the update endpoint
    const uploadUrl =
      editQuestionIndex !== null && newQuestion.id
        ? `${api.baseURL}/uploads/questionImage/${newQuestion.id}`
        : `${api.baseURL}/uploads/questionImage`;

    const method =
      editQuestionIndex !== null && newQuestion.id ? "PUT" : "POST";

    // Upload the image to our server
    fetch(uploadUrl, {
      method: method,
      body: formData,
      credentials: "include",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          // Update the question with the image URL
          setNewQuestion({
            ...newQuestion,
            image: data.imageUrl,
          });
          toast.success("Image uploaded successfully");
        } else {
          toast.error(data.message || "Failed to upload image");
        }
      })
      .catch((error) => {
        console.error("Error uploading image:", error);
        toast.error("Error uploading image");

        // Remove the preview if there was an error
        setImagePreview(null);
      });
  };

  // Remove uploaded image
  const removeImage = () => {
    setImagePreview(null);
    setNewQuestion({
      ...newQuestion,
      image: "",
    });
  };

  // Save question (add new or update existing)
  const saveQuestion = async () => {
    // Form validation
    if (!newQuestion.questionText.trim()) {
      toast.error("Question text is required");
      return;
    }

    if (newQuestion.options.some((opt) => !opt.trim())) {
      toast.error("All options must be filled");
      return;
    }

    if (!newQuestion.explanation.trim()) {
      toast.error("Explanation is required");
      return;
    }

    if (!newQuestion.categoryId) {
      toast.error("Category is required");
      return;
    }

    try {
      setLoading(true);

      // Prepare question data
      const questionData = {
        questionText: newQuestion.questionText,
        options: newQuestion.options,
        correctAnswer: newQuestion.correctAnswer,
        explanation: newQuestion.explanation,
        image: newQuestion.image,
        categoryId: newQuestion.categoryId,
        subcategoryId: newQuestion.subcategoryId || undefined, // Don't send empty string
      };

      let response;
      if (editQuestionIndex !== null) {
        // Update existing question
        response = await api.practiceQuestions.update(
          newQuestion.id,
          questionData
        );
      } else {
        // Add new question
        response = await api.practiceQuestions.create(questionData);
      }

      if (response.success) {
        if (editQuestionIndex !== null) {
          // Update question in state
          setPracticeMCQs(
            practiceMCQs.map((q) =>
              q.id === newQuestion.id ? response.data : q
            )
          );
          toast.success("Question updated successfully");
        } else {
          // Add question to state
          setPracticeMCQs([...practiceMCQs, response.data]);
          toast.success("Question added successfully");
        }

        setShowAddModal(false);
      } else {
        setError(response.message || "Failed to save question");
        toast.error(response.message || "Failed to save question");
      }
    } catch (err) {
      setError("Error connecting to the server");
      console.error("Error saving question:", err);
      toast.error("Error connecting to the server");
    } finally {
      setLoading(false);
    }
  };

  // Import questions from CSV
  const importFromCSV = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv";

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const csvData = event.target.result;
          const lines = csvData
            .split("\n")
            .filter((line) => line.trim() !== "");

          const importedQuestions = [];
          const errors = [];

          for (let i = 0; i < lines.length; i++) {
            try {
              const line = lines[i];
              const values = line.split(",").map((val) => val.trim());

              if (values.length < 8) {
                errors.push(`Line ${i + 1}: Insufficient data`);
                continue;
              }

              const [
                questionText,
                opt1,
                opt2,
                opt3,
                opt4,
                correctAnswerStr,
                categoryName,
                subcategoryName,
                explanation,
                imageUrl,
              ] = values;

              const correctAnswer = parseInt(correctAnswerStr, 10);

              if (
                isNaN(correctAnswer) ||
                correctAnswer < 0 ||
                correctAnswer > 3
              ) {
                errors.push(`Line ${i + 1}: Invalid correct answer index`);
                continue;
              }

              // Find category ID by name
              const category = categories.find(
                (c) => c.name.toLowerCase() === categoryName.toLowerCase()
              );

              if (!category) {
                errors.push(
                  `Line ${i + 1}: Category "${categoryName}" not found`
                );
                continue;
              }

              // Find subcategory ID by name
              let subcategoryId = null;
              if (subcategoryName && subcategoryName.trim() !== "") {
                const categorySubcategories = subcategories[category.id] || [];
                const subcategory = categorySubcategories.find(
                  (sc) =>
                    sc.name.toLowerCase() === subcategoryName.toLowerCase()
                );

                if (subcategory) {
                  subcategoryId = subcategory.id;
                } else {
                  errors.push(
                    `Line ${
                      i + 1
                    }: Subcategory "${subcategoryName}" not found under category "${categoryName}"`
                  );
                  continue;
                }
              }

              importedQuestions.push({
                questionText,
                options: [opt1, opt2, opt3, opt4],
                correctAnswer,
                explanation:
                  explanation ||
                  `Correct answer is: ${
                    [opt1, opt2, opt3, opt4][correctAnswer]
                  }`,
                image: imageUrl || "",
                categoryId: category.id,
                subcategoryId: subcategoryId,
              });
            } catch (err) {
              errors.push(`Line ${i + 1}: ${err.message}`);
            }
          }

          if (errors.length > 0) {
            toast.error(`${errors.length} errors found in CSV`);
            console.error("CSV import errors:", errors);
          }

          if (importedQuestions.length > 0) {
            try {
              setLoading(true);
              const response = await api.practiceQuestions.bulkCreate(
                importedQuestions
              );

              if (response.success) {
                // Add questions to state
                setPracticeMCQs([...practiceMCQs, ...response.data]);
                toast.success(
                  `Successfully imported ${response.data.length} questions`
                );
              } else {
                setError(response.message || "Failed to import questions");
                toast.error(response.message || "Failed to import questions");
              }
            } catch (err) {
              setError("Error connecting to the server");
              console.error("Error importing questions:", err);
              toast.error("Error connecting to the server");
            } finally {
              setLoading(false);
            }
          }
        } catch (error) {
          toast.error(`Error importing CSV: ${error.message}`);
        }
      };

      reader.readAsText(file);
    };

    input.click();
  };

  // Export questions to CSV
  const exportToCSV = () => {
    const questions = selectedCategory === "All" ? practiceMCQs : filteredMCQs;

    if (questions.length === 0) {
      toast.warning("No questions to export");
      return;
    }

    const csvContent = questions
      .map((q) => {
        // Get category and subcategory name
        const category = categories.find((c) => c.id === q.categoryId);
        let subcategory = null;
        if (q.subcategoryId && category) {
          const categorySubcategories = subcategories[category.id] || [];
          subcategory = categorySubcategories.find(
            (sc) => sc.id === q.subcategoryId
          );
        }

        return `${q.questionText.replace(/,/g, ";")},${q.options
          .map((opt) => opt.replace(/,/g, ";"))
          .join(",")},${q.correctAnswer},${
          category ? category.name.replace(/,/g, ";") : ""
        },${subcategory ? subcategory.name.replace(/,/g, ";") : ""},${
          q.explanation ? q.explanation.replace(/,/g, ";") : ""
        },${q.image ? q.image.replace(/,/g, ";") : ""}`;
      })
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `practice_mcqs_${new Date().toISOString().split("T")[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get category name by ID
  const getCategoryName = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    return category ? category.name : "Unknown Category";
  };

  // Get subcategory name by ID
  const getSubcategoryName = (categoryId, subcategoryId) => {
    if (!subcategoryId) return "";
    const categorySubcategories = subcategories[categoryId] || [];
    const subcategory = categorySubcategories.find(
      (sc) => sc.id === subcategoryId
    );
    return subcategory ? subcategory.name : "Unknown Subcategory";
  };

  // View question details
  const viewQuestionDetails = (index) => {
    setSelectedQuestion(filteredMCQs[index]);
    setShowViewModal(true);
  };

  if (loading && categories.length === 0) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div
                className="card-body d-flex justify-content-center align-items-center"
                style={{ height: "300px" }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && categories.length === 0) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="alert alert-danger">{error}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header border-bottom bg-base py-16 px-24">
              <h6 className="text-lg fw-semibold mb-0">Manage Practice MCQs</h6>
            </div>
            <div className="card-body p-24">
              {/* Filter and Search Section */}
              <div className="row mb-4">
                <div className="col-lg-3 mb-3">
                  <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                    Filter by Category
                  </label>
                  <select
                    className="form-select"
                    value={selectedCategoryId || "All"}
                    onChange={handleCategoryChange}
                  >
                    <option value="All">All Categories</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-3 mb-3">
                  <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                    Filter by Subcategory
                  </label>
                  <select
                    className="form-select"
                    value={selectedSubcategoryId || "All"}
                    onChange={handleSubcategoryChange}
                    disabled={
                      !selectedCategoryId || selectedCategoryId === "All"
                    }
                  >
                    <option value="All">All Subcategories</option>
                    {selectedCategoryId &&
                      selectedCategoryId !== "All" &&
                      subcategories[selectedCategoryId] &&
                      subcategories[selectedCategoryId].map((subcategory) => (
                        <option key={subcategory.id} value={subcategory.id}>
                          {subcategory.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="col-lg-6 mb-3">
                  <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                    Search Questions
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by question, option or explanation"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setSearchTerm("")}
                    >
                      <Icon icon="mdi:close" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h6 className="mb-0">
                    Practice Questions: {filteredMCQs.length}
                  </h6>
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-primary" onClick={addQuestion}>
                    <Icon icon="mdi:plus" className="me-1" />
                    Add Question
                  </button>
                  <button className="btn btn-success" onClick={importFromCSV}>
                    <Icon icon="mdi:file-upload" className="me-1" />
                    Import CSV
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={exportToCSV}
                    disabled={filteredMCQs.length === 0}
                  >
                    <Icon icon="mdi:file-download" className="me-1" />
                    Export CSV
                  </button>
                </div>
              </div>

              {/* Questions Table */}
              {loading ? (
                <div className="d-flex justify-content-center my-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : filteredMCQs.length === 0 ? (
                <div className="alert alert-info">
                  No practice questions found matching your criteria.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-bordered">
                    <thead className="bg-light">
                      <tr>
                        <th width="5%">#</th>
                        <th width="35%">Question</th>
                        <th width="10%">Correct Answer</th>
                        <th width="15%">Category</th>
                        <th width="15%">Subcategory</th>
                        <th width="20%">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMCQs.map((mcq, index) => (
                        <tr key={mcq.id}>
                          <td>{index + 1}</td>
                          <td>
                            <div
                              style={{
                                maxWidth: "400px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={mcq.questionText}
                            >
                              {mcq.image && (
                                <Icon
                                  icon="mdi:image"
                                  className="me-1 text-primary"
                                  title="This question has an image"
                                  style={{ verticalAlign: "middle" }}
                                />
                              )}
                              {mcq.questionText}
                            </div>
                          </td>
                          <td>
                            {mcq.options[mcq.correctAnswer]?.length > 20
                              ? mcq.options[mcq.correctAnswer].substring(
                                  0,
                                  20
                                ) + "..."
                              : mcq.options[mcq.correctAnswer]}
                          </td>
                          <td>
                            <span className="badge bg-primary">
                              {getCategoryName(mcq.categoryId)}
                            </span>
                          </td>
                          <td>
                            {mcq.subcategoryId && (
                              <span className="badge bg-secondary">
                                {getSubcategoryName(
                                  mcq.categoryId,
                                  mcq.subcategoryId
                                )}
                              </span>
                            )}
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => viewQuestionDetails(index)}
                              >
                                <Icon icon="mdi:eye" />
                                View
                              </button>
                              <button
                                className="btn btn-sm btn-info"
                                onClick={() => editQuestion(index)}
                              >
                                <Icon icon="mdi:pencil" />
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => deleteQuestion(mcq.id)}
                              >
                                <Icon icon="mdi:delete" />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Question Modal */}
      {showAddModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editQuestionIndex !== null
                    ? "Edit Practice Question"
                    : "Add New Practice Question"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAddModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-12 mb-3">
                    <label className="form-label fw-semibold">Question</label>
                    <textarea
                      className="form-control"
                      name="questionText"
                      value={newQuestion.questionText}
                      onChange={handleQuestionChange}
                      rows="2"
                      required
                    ></textarea>
                  </div>
                </div>

                {/* Image Upload Section */}
                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label fw-semibold">
                      Question Image (Optional)
                    </label>
                    <div className="d-flex align-items-center gap-3">
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                      {imagePreview && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={removeImage}
                        >
                          <Icon icon="mdi:delete" /> Remove
                        </button>
                      )}
                    </div>
                    <small className="text-muted">
                      Upload an image related to the question (max 5MB)
                    </small>
                  </div>
                </div>

                {/* Image Preview */}
                {imagePreview && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <div className="card">
                        <div className="card-body">
                          <img
                            src={imagePreview}
                            alt="Question preview"
                            className="img-fluid"
                            style={{ maxHeight: "200px" }}
                            onError={(e) => {
                              console.error(
                                "Image preview failed to load:",
                                imagePreview
                              );
                              e.target.onerror = null;
                              e.target.src =
                                "https://via.placeholder.com/400x200?text=Image+Preview+Error";
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="row mb-3">
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Category</label>
                    <select
                      className="form-select"
                      name="categoryId"
                      value={newQuestion.categoryId}
                      onChange={handleQuestionChange}
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">
                      Subcategory
                    </label>
                    <select
                      className="form-select"
                      name="subcategoryId"
                      value={newQuestion.subcategoryId || ""}
                      onChange={handleQuestionChange}
                    >
                      <option value="">No Subcategory</option>
                      {newQuestion.categoryId &&
                        subcategories[newQuestion.categoryId] &&
                        subcategories[newQuestion.categoryId].map(
                          (subcategory) => (
                            <option key={subcategory.id} value={subcategory.id}>
                              {subcategory.name}
                            </option>
                          )
                        )}
                    </select>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Options</label>
                  {newQuestion.options.map((option, index) => (
                    <div key={index} className="input-group mb-2">
                      <div
                        className="input-group-text"
                        style={{
                          minWidth: "40px",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <input
                          type="radio"
                          name="correctAnswer"
                          className="form-check-input mt-0"
                          style={{
                            visibility: "visible",
                            opacity: 1,
                            position: "relative",
                            margin: "0",
                          }}
                          checked={newQuestion.correctAnswer === index}
                          onChange={() =>
                            setNewQuestion({
                              ...newQuestion,
                              correctAnswer: index,
                            })
                          }
                        />
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={`Option ${index + 1}`}
                        value={option}
                        onChange={(e) =>
                          handleOptionChange(index, e.target.value)
                        }
                        required
                      />
                    </div>
                  ))}
                  <small className="text-muted">
                    Select the radio button next to the correct answer
                  </small>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Explanation</label>
                  <textarea
                    className="form-control"
                    name="explanation"
                    value={newQuestion.explanation}
                    onChange={handleQuestionChange}
                    rows="3"
                    required
                    placeholder="Explain why the correct answer is right"
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={saveQuestion}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {editQuestionIndex !== null ? "Updating..." : "Adding..."}
                    </>
                  ) : editQuestionIndex !== null ? (
                    "Update Question"
                  ) : (
                    "Add Question"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Question Details Modal */}
      {showViewModal && selectedQuestion && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">View Practice Question</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowViewModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {/* Question */}
                <div className="mb-4">
                  <h6 className="fw-bold mb-2">Question:</h6>
                  <p>{selectedQuestion.questionText}</p>
                </div>

                {/* Image (if available) */}
                {selectedQuestion.image && (
                  <div className="mb-4">
                    <h6 className="fw-bold mb-2">Question Image:</h6>
                    <img
                      src={
                        selectedQuestion.image.startsWith("http")
                          ? selectedQuestion.image
                          : `${api.baseURL.replace("/api", "")}${
                              selectedQuestion.image
                            }`
                      }
                      alt="Question"
                      className="img-fluid rounded border"
                      style={{ maxHeight: "300px" }}
                      onError={(e) => {
                        console.error(
                          "Image failed to load:",
                          selectedQuestion.image
                        );
                        e.target.onerror = null;
                        e.target.src =
                          "https://via.placeholder.com/400x200?text=Image+Not+Found";
                      }}
                    />
                  </div>
                )}

                {/* Options */}
                <div className="mb-4">
                  <h6 className="fw-bold mb-2">Options:</h6>
                  <ul className="list-group">
                    {selectedQuestion.options.map((option, idx) => (
                      <li
                        key={idx}
                        className={`list-group-item ${
                          idx === selectedQuestion.correctAnswer
                            ? "list-group-item-success"
                            : ""
                        }`}
                      >
                        {idx === selectedQuestion.correctAnswer && (
                          <Icon
                            icon="mdi:check-circle"
                            className="me-1 text-success"
                          />
                        )}
                        {option}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Explanation */}
                <div className="mb-4">
                  <h6 className="fw-bold mb-2">Explanation:</h6>
                  <p>{selectedQuestion.explanation}</p>
                </div>

                {/* Category & Subcategory */}
                <div className="mb-4">
                  <h6 className="fw-bold mb-2">Category:</h6>
                  <span className="badge bg-primary">
                    {getCategoryName(selectedQuestion.categoryId)}
                  </span>
                  {selectedQuestion.subcategoryId && (
                    <>
                      <h6 className="fw-bold mt-2 mb-2">Subcategory:</h6>
                      <span className="badge bg-secondary">
                        {getSubcategoryName(
                          selectedQuestion.categoryId,
                          selectedQuestion.subcategoryId
                        )}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowViewModal(false);
                    setEditQuestionIndex(
                      filteredMCQs.findIndex(
                        (q) => q.id === selectedQuestion.id
                      )
                    );
                    editQuestion(
                      filteredMCQs.findIndex(
                        (q) => q.id === selectedQuestion.id
                      )
                    );
                  }}
                >
                  Edit Question
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PracticeMCQsLayer;
